import { useCallback, useEffect, useMemo, useState } from "react";
import moment from "moment";
import { useIntl } from "react-intl";
import { Notification } from "@rio-cloud/rio-uikit";
import Dialog from "@rio-cloud/rio-uikit/Dialog";
import { MonthNavigation } from "@components/Dashboard";
import SelectedFilters from "@components/SelectedFilters";
import KpiCard, { KpiCardDelta, KpiCardIcon } from "@components/Dashboard/KPICards/KpiCard";
import KpiInfoTooltip, { formatPeriodRange } from "@components/shared/KpiInfoTooltip";
import KpiComparisonTooltip from "@components/shared/KpiComparisonTooltip";
import KpiCardsSkeleton from "@components/Skeletons/KpiCardsSkeleton";
import HorizontalBarListSkeleton from "@components/Skeletons/HorizontalBarListSkeleton";
import TopVehiclesMaintenanceTableSkeleton from "@components/Skeletons/TopVehiclesMaintenanceTableSkeleton";
import MaintenanceAlertsCard from "@components/Maintenance/AlertsCard";
import HorizontalBarList, { HorizontalBarItem } from "@components/Maintenance/HorizontalBarList";
import TopVehiclesMaintenanceTable from "@components/Maintenance/TopVehiclesMaintenanceTable";
import MaintenanceTable from "@components/Maintenance/MaintenanceTable";
import MaintenanceDetail from "@components/Maintenance/MaintenanceDetail";
import {
  EMPTY_MAINTENANCE_SUMMARY,
  exportMaintenance,
  getMaintenanceAlertTotals,
  getMaintenanceByType,
  getMaintenanceSummary,
  getMaintenanceTransactions,
  getTopMaintenanceSubcategories,
  getTopMaintenanceVehicles,
  MaintenanceAlertTotals,
  MaintenancePeriod,
  MaintenanceSummary,
  MaintenanceTransaction,
  MaintenanceTypeTotal,
  MaintenanceVehicleCost,
} from "@services/maintenance";
import { setEndDate, setStartDate, useGetStoredEndDate, useGetStoredStartDate } from "@store/date/dateSlice";
import { useAppDispatch } from "@store";
import { useIsTreeReady } from "@store/tmsAssetTree/tmsAssetTreeSlice";
import { useRequestFilters, useStoredFilterValues } from "@hooks/useStoredFilters";
import { useMonthNavigation } from "@hooks/useMonthNavigation";
import { useDebounce, useT } from "@hooks";
import { toKpiComparison } from "@utils/kpiComparison";

const PAGE_SIZE = 10;
const SEARCH_DEBOUNCE = 2000;
const TOP_LIMIT = 5;
const BY_TYPE_COLOR = "#5C9EBE";
const SUBCATEGORY_COLOR = "#F2907F";
const SUBCATEGORY_LABEL_WIDTH = 130;
const KPI_CARDS = 2;
const SUBCATEGORIES_TITLE_SKELETON_WIDTH = 140;

const formatAmount = (value: number) =>
  value.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const formatCurrency = (value: number) => `R$ ${formatAmount(value)}`;

const formatInteger = (value: number) => value.toLocaleString("pt-BR");

const resolveMomentLocale = (locale: string): string => {
  if (locale.startsWith("en")) return "en-gb";
  if (locale.startsWith("es")) return "es";
  return "pt-br";
};

const MaintenanceScreen = () => {
  const t = useT();
  const { locale } = useIntl();
  const momentLocale = resolveMomentLocale(locale);
  moment.locale(momentLocale);
  const dispatch = useAppDispatch();

  const startDate = useGetStoredStartDate();
  const endDate = useGetStoredEndDate();
  const kpiPeriodRange = formatPeriodRange(startDate, endDate);
  const isTreeReady = useIsTreeReady();
  const storedFilters = useStoredFilterValues();

  const { showDateRangePicker, setShowDateRangePicker, currentMonth, goToPrevMonth, goToNextMonth } =
    useMonthNavigation({ startDate, endDate, momentLocale, dispatch });

  const requestFilters = useRequestFilters(storedFilters, startDate, endDate);
  const period = useMemo<MaintenancePeriod>(() => ({ startDate, endDate }), [startDate, endDate]);

  const [summary, setSummary] = useState<MaintenanceSummary>(EMPTY_MAINTENANCE_SUMMARY);
  const [isLoadingSummary, setIsLoadingSummary] = useState(true);
  const [byType, setByType] = useState<MaintenanceTypeTotal[]>([]);
  const [isLoadingByType, setIsLoadingByType] = useState(true);
  const [subcategories, setSubcategories] = useState<HorizontalBarItem[]>([]);
  const [isLoadingSubcategories, setIsLoadingSubcategories] = useState(true);
  const [vehicles, setVehicles] = useState<MaintenanceVehicleCost[]>([]);
  const [isLoadingVehicles, setIsLoadingVehicles] = useState(true);
  const [alertTotals, setAlertTotals] = useState<MaintenanceAlertTotals | undefined>(undefined);
  const [transactions, setTransactions] = useState<MaintenanceTransaction[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoadingTable, setIsLoadingTable] = useState(true);
  const [tableRefreshKey, setTableRefreshKey] = useState(0);
  const [searchValue, setSearchValue] = useState("");
  const debouncedSearchValue = useDebounce(searchValue, SEARCH_DEBOUNCE);
  const [detailTransaction, setDetailTransaction] = useState<MaintenanceTransaction | null>(null);

  const hasPeriod = Boolean(startDate && endDate);

  useEffect(() => {
    let cancelled = false;
    if (!hasPeriod || !isTreeReady) return;
    setIsLoadingSummary(true);
    setIsLoadingByType(true);

    getMaintenanceSummary(requestFilters)
      .then((result) => {
        if (!cancelled) setSummary(result);
      })
      .finally(() => {
        if (!cancelled) setIsLoadingSummary(false);
      });
    getMaintenanceByType(requestFilters)
      .then((items) => {
        if (!cancelled) setByType(items);
      })
      .finally(() => {
        if (!cancelled) setIsLoadingByType(false);
      });

    return () => {
      cancelled = true;
    };
  }, [hasPeriod, isTreeReady, requestFilters]);

  useEffect(() => {
    let cancelled = false;
    if (!hasPeriod || !isTreeReady) return;
    setIsLoadingSubcategories(true);
    setIsLoadingVehicles(true);
    setAlertTotals(undefined);

    getTopMaintenanceSubcategories(period, TOP_LIMIT)
      .then((items) => {
        if (cancelled) return;
        setSubcategories(items.map((item) => ({ label: item.subcategory, value: item.amount })));
      })
      .finally(() => {
        if (!cancelled) setIsLoadingSubcategories(false);
      });
    getTopMaintenanceVehicles(period, TOP_LIMIT)
      .then((items) => {
        if (!cancelled) setVehicles(items);
      })
      .finally(() => {
        if (!cancelled) setIsLoadingVehicles(false);
      });
    getMaintenanceAlertTotals(period).then((totals) => {
      if (!cancelled) setAlertTotals(totals);
    });

    return () => {
      cancelled = true;
    };
  }, [hasPeriod, isTreeReady, period]);

  useEffect(() => {
    setCurrentPage(1);
  }, [requestFilters, debouncedSearchValue]);

  useEffect(() => {
    let cancelled = false;
    if (!hasPeriod || !isTreeReady) return;
    setIsLoadingTable(true);
    getMaintenanceTransactions(requestFilters, currentPage - 1, PAGE_SIZE, debouncedSearchValue)
      .then((result) => {
        if (cancelled) return;
        setTransactions(result.content);
        setTotalPages(Math.max(1, result.totalPages));
      })
      .finally(() => {
        if (!cancelled) setIsLoadingTable(false);
      });
    return () => {
      cancelled = true;
    };
  }, [hasPeriod, isTreeReady, requestFilters, currentPage, tableRefreshKey, debouncedSearchValue]);

  const byTypeItems: HorizontalBarItem[] = byType.map((item) => ({
    label: t(`maintenance.type.${item.type}`),
    value: item.amount,
  }));

  const handleRefresh = useCallback(() => {
    setTableRefreshKey((key) => key + 1);
  }, []);

  const handleDownload = useCallback(async () => {
    try {
      await exportMaintenance(requestFilters);
      Notification.success(t("maintenance.notification.exportSuccess"));
    } catch {
      Notification.error(t("maintenance.notification.exportError"));
    }
  }, [requestFilters, t]);

  const handleViewDetail = useCallback(
    (id: string) => {
      const transaction = transactions.find((item) => item.id === id);

      if (!transaction) {
        Notification.error(t("maintenance.notification.detailError"));
        return;
      }

      setDetailTransaction(transaction);
    },
    [transactions, t]
  );

  const handleCloseDetail = useCallback(() => setDetailTransaction(null), []);

  return (
    <div className="padding-10 padding-20-sm display-flex flex-column gap-10 gap-20-sm min-height-100pct">
      <MonthNavigation
        endDate={endDate}
        startDate={startDate}
        currentMonth={currentMonth}
        onPrevMonth={goToPrevMonth}
        onNextMonth={goToNextMonth}
        onEndDateChange={(date) => dispatch(setEndDate(date))}
        onStartDateChange={(date) => dispatch(setStartDate(date))}
        showDateRangePicker={showDateRangePicker}
        onClick={() => setShowDateRangePicker(!showDateRangePicker)}
        onCloseDateRangePicker={() => setShowDateRangePicker(false)}
      />
      <SelectedFilters requestFilters={requestFilters} />

      {isLoadingSummary ? (
        <KpiCardsSkeleton
          cards={KPI_CARDS}
          className="display-grid gap-10 gap-20-sm grid-cols-1 grid-cols-2-md"
          style={{ gridTemplateColumns: "repeat(2, 1fr)" }}
          contentGapClassName="gap-20"
        />
      ) : (
        <div
          className="display-grid gap-10 gap-20-sm grid-cols-1 grid-cols-2-md"
          style={{ gridTemplateColumns: "repeat(2, 1fr)" }}
        >
          <KpiCard
            label={
              <>
                {t("maintenance.kpi.totalSpent")}
                <KpiInfoTooltip
                  title={t("maintenance.kpi.totalSpent")}
                  description={t("maintenance.kpi.totalSpent.tooltip")}
                  lines={[formatCurrency(summary.totalSpent.value)]}
                  periodRange={kpiPeriodRange}
                />
              </>
            }
            value={formatAmount(summary.totalSpent.value)}
            icon={<KpiCardIcon />}
            contentGapClassName="gap-20"
            footer={
              <KpiComparisonTooltip comparison={toKpiComparison(summary.totalSpent)} formatValue={formatCurrency}>
                <KpiCardDelta deltaPercentage={summary.totalSpent.variationPercent} vsLabel={t("kpi.vsLastMonth")} />
              </KpiComparisonTooltip>
            }
          />
          <div className="display-none">
            <KpiCard
              label={
                <>
                  {t("maintenance.kpi.avgCostPerKm")}
                  <KpiInfoTooltip
                    title={t("maintenance.kpi.avgCostPerKm")}
                    description={t("maintenance.kpi.avgCostPerKm.tooltip")}
                    lines={[formatCurrency(summary.averageCostPerKm.value)]}
                    periodRange={kpiPeriodRange}
                  />
                </>
              }
              value={formatAmount(summary.averageCostPerKm.value)}
              icon={<KpiCardIcon />}
              contentGapClassName="gap-20"
              footerGapClassName="gap-15"
              footer={
                <>
                  <span className="display-flex align-items-center">
                    <KpiComparisonTooltip
                      comparison={toKpiComparison(summary.averageCostPerKm)}
                      formatValue={formatCurrency}
                    >
                      <KpiCardDelta
                        deltaPercentage={summary.averageCostPerKm.variationPercent}
                        vsLabel={t("kpi.vsLastMonth")}
                      />
                    </KpiComparisonTooltip>
                  </span>
                  <span className="text-color-gray">
                    <span className="text-medium text-color-success">
                      {formatInteger(summary.averageCostPerKm.kmDriven)}
                    </span>{" "}
                    {t("maintenance.kpi.kmDriven")}
                  </span>
                </>
              }
            />
          </div>
          <KpiCard
            label={
              <>
                {t("maintenance.kpi.avgCostPerVehicle")}
                <KpiInfoTooltip
                  title={t("maintenance.kpi.avgCostPerVehicle")}
                  description={t("maintenance.kpi.avgCostPerVehicle.tooltip")}
                  lines={[formatCurrency(summary.averageCostPerVehicle.value)]}
                  periodRange={kpiPeriodRange}
                  placement="bottom-end"
                />
              </>
            }
            value={formatAmount(summary.averageCostPerVehicle.value)}
            icon={<KpiCardIcon />}
            contentGapClassName="gap-20"
            footer={
              <KpiComparisonTooltip
                comparison={toKpiComparison(summary.averageCostPerVehicle)}
                formatValue={formatCurrency}
                placement="bottom-end"
              >
                <KpiCardDelta
                  deltaPercentage={summary.averageCostPerVehicle.variationPercent}
                  vsLabel={t("kpi.vsLastMonth")}
                />
              </KpiComparisonTooltip>
            }
          />
        </div>
      )}

      <div
        className="display-grid gap-10 gap-20-sm grid-cols-1 grid-cols-2-lg"
        style={{ gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)" }}
      >
        <MaintenanceAlertsCard totals={alertTotals} period={period} />
        {isLoadingByType ? (
          <HorizontalBarListSkeleton items={3} />
        ) : (
          <HorizontalBarList title={t("maintenance.chart.byType")} items={byTypeItems} color={BY_TYPE_COLOR} />
        )}
      </div>

      <div
        className="display-grid gap-10 gap-20-sm grid-cols-1 grid-cols-2-lg"
        style={{ gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)" }}
      >
        {isLoadingSubcategories ? (
          <HorizontalBarListSkeleton
            labelWidth={SUBCATEGORY_LABEL_WIDTH}
            titleWidth={SUBCATEGORIES_TITLE_SKELETON_WIDTH}
          />
        ) : (
          <HorizontalBarList
            title={t("maintenance.chart.topSubcategories")}
            items={subcategories}
            color={SUBCATEGORY_COLOR}
            labelWidth={SUBCATEGORY_LABEL_WIDTH}
          />
        )}
        {isLoadingVehicles ? <TopVehiclesMaintenanceTableSkeleton /> : <TopVehiclesMaintenanceTable items={vehicles} />}
      </div>

      <MaintenanceTable
        data={transactions}
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
        isLoading={isLoadingTable}
        onRefresh={handleRefresh}
        onDownload={handleDownload}
        onViewDetail={handleViewDetail}
        searchValue={searchValue}
        onSearchValueChange={setSearchValue}
      />

      <Dialog
        show={detailTransaction !== null}
        title={<span className="text-medium text-size-16">{t("maintenance.dialog.detailTitle")}</span>}
        body={detailTransaction ? <MaintenanceDetail transaction={detailTransaction} /> : null}
        footer={
          <div className="display-flex flex-wrap gap-10 justify-content-end">
            <button type="button" className="btn btn-default width-100pct-xs" onClick={handleCloseDetail}>
              {t("common.action.close")}
            </button>
          </div>
        }
        onClose={handleCloseDetail}
        showCloseButton
        bsSize="md"
      />
    </div>
  );
};

export default MaintenanceScreen;
